import { useState, useEffect, useRef } from "react";
import { Heart } from "lucide-react";
import { doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../../../firebase";
import "./LikeButton.css";

const STORAGE_KEY = "likedProducts";

const readStoredLikes = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeStoredLikes = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (error) {
    /* localStorage может быть недоступен — игнорируем */
  }
};

/**
 * Лайк для карточки товара.
 *
 * - `productId`      — id документа товара (обязательно);
 * - `collectionName` — коллекция Firestore, где лежит товар
 *                      ("products" для тортов, "store_products" для магазина);
 * - `initialLikes`   — текущее число лайков, приходит вместе с товаром из базы.
 *
 * Лайк конкретного пользователя хранится в localStorage по составному ключу
 * `${collectionName}:${productId}`, а счётчик — в поле `likes` самого товара.
 */
export default function LikeButton({
  productId,
  collectionName = "products",
  initialLikes = 0,
  className = "",
}) {
  const itemKey = `${collectionName}:${productId}`;

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(() => Number(initialLikes) || 0);
  const [isError, setIsError] = useState(false);
  const busyRef = useRef(false);

  // Восстанавливаем состояние лайка из localStorage
  useEffect(() => {
    setLiked(readStoredLikes().includes(itemKey));
  }, [itemKey]);

  // Синхронизация лайка между вкладками браузера
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      try {
        const next = JSON.parse(event.newValue || "[]");
        if (Array.isArray(next)) {
          setLiked(next.includes(itemKey));
        }
      } catch (error) {
        /* невалидное значение — пропускаем */
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [itemKey]);

  const toggleLike = async () => {
    if (!productId || busyRef.current) return;
    busyRef.current = true;
    setIsError(false);

    const wasLiked = liked;
    const nextLiked = !wasLiked;
    const nextCount = Math.max(0, likeCount + (nextLiked ? 1 : -1));

    // Оптимистичное обновление интерфейса
    setLiked(nextLiked);
    setLikeCount(nextCount);

    const stored = readStoredLikes();
    const updated = nextLiked
      ? [...stored.filter((key) => key !== itemKey), itemKey]
      : stored.filter((key) => key !== itemKey);
    writeStoredLikes(updated);

    try {
      await updateDoc(doc(db, collectionName, productId), {
        likes: increment(nextLiked ? 1 : -1),
      });
    } catch (error) {
      // Если запись не удалась (например, правила Firestore запрещают),
      // откатываем интерфейс и localStorage, чтобы не обманывать пользователя
      console.error("Не удалось сохранить лайк:", error);
      setLiked(wasLiked);
      setLikeCount(likeCount);
      writeStoredLikes(stored);
      setIsError(true);
      setTimeout(() => setIsError(false), 1200);
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <button
      type="button"
      className={`like-wrapper ${liked ? "liked" : ""} ${isError ? "error" : ""} ${className}`.trim()}
      onClick={toggleLike}
      aria-pressed={liked}
      aria-label={liked ? "Убрать лайк" : "Поставить лайк"}
      title={liked ? "Убрать лайк" : "Поставить лайк"}
      disabled={!productId}
    >
      <Heart className="like-icon" size={20} />
      <span className="like-count">{likeCount}</span>
    </button>
  );
}
